import {
  getLeoUuid,
  LeoUUID,
  getBoolean,
  getObject,
  getOptionalObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { CurrentRespondentScreenDetails } from "./currentRespondentScreenDetails";
import { RespondentSurveySettings, RespondentSurveySettingsDTO } from "./respondentSurveySettings";
import { ProgressPercentage, ProgressPercentageDTO } from "./progressPercentage";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
/* eslint-disable import/export */
export abstract class GetCurrentQuestionAndSettingsRPC implements
  LeoRPC<GetCurrentQuestionAndSettingsRPC.Request, GetCurrentQuestionAndSettingsRPC.Response, GetCurrentQuestionAndSettingsRPC.Errors.Errors>
{
  abstract execute(
    request: GetCurrentQuestionAndSettingsRPC.Request
  ): Promise<LeoRPCResult<GetCurrentQuestionAndSettingsRPC.Response, GetCurrentQuestionAndSettingsRPC.Errors.Errors>>;
}

export namespace GetCurrentQuestionAndSettingsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyLinkId: string;
    isPreviewURL: boolean;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetCurrentQuestionAndSettingsRPCRequest: boolean = true;
  
    readonly surveyLinkId: LeoUUID;
    readonly isPreviewURL: boolean;
  
  
    constructor(
    surveyLinkId: LeoUUID, 
    isPreviewURL: boolean
    ) {
      this.surveyLinkId = surveyLinkId;
      this.isPreviewURL = isPreviewURL;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyLinkId = getLeoUuid(dto, "surveyLinkId", InvalidRequestSurveyLinkIdError);
      const isPreviewURL = getBoolean(dto, "isPreviewURL", InvalidRequestIsPreviewUrlError);
      return new Request(
        surveyLinkId,
        isPreviewURL
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyLinkId: this.surveyLinkId.toDTO(),
          isPreviewURL: this.isPreviewURL
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetCurrentQuestionAndSettingsRPC(surveyLinkId='${this.surveyLinkId}',isPreviewURL='${this.isPreviewURL}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    currentRespondentScreenDetails: CurrentRespondentScreenDetails.CurrentRespondentScreenDetailsDTO;
    respondentSurveySettings: RespondentSurveySettingsDTO;
    progressPercentage: ProgressPercentageDTO;
    isWelcomePageShown: boolean;
    hasRespondentConsented: boolean;
    submitConfirmationButtonText?: SurveyLocalizedTextDTO | null;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetCurrentQuestionAndSettingsResponse: boolean = true;
  
    readonly currentRespondentScreenDetails: CurrentRespondentScreenDetails.CurrentRespondentScreenDetails;
    readonly respondentSurveySettings: RespondentSurveySettings;
    readonly progressPercentage: ProgressPercentage;
    readonly isWelcomePageShown: boolean;
    readonly hasRespondentConsented: boolean;
    readonly submitConfirmationButtonText: SurveyLocalizedText | null;
  
  
    constructor(
    currentRespondentScreenDetails: CurrentRespondentScreenDetails.CurrentRespondentScreenDetails, 
    respondentSurveySettings: RespondentSurveySettings, 
    progressPercentage: ProgressPercentage, 
    isWelcomePageShown: boolean, 
    hasRespondentConsented: boolean, 
    submitConfirmationButtonText: SurveyLocalizedText | null = null
    ) {
      this.currentRespondentScreenDetails = currentRespondentScreenDetails;
      this.respondentSurveySettings = respondentSurveySettings;
      this.progressPercentage = progressPercentage;
      this.isWelcomePageShown = isWelcomePageShown;
      this.hasRespondentConsented = hasRespondentConsented;
      this.submitConfirmationButtonText = submitConfirmationButtonText;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const currentRespondentScreenDetails = CurrentRespondentScreenDetails.fromDTO(getObject(dto, "currentRespondentScreenDetails", InvalidResponseCurrentRespondentScreenDetailsError));
      const respondentSurveySettings = RespondentSurveySettings.fromDTO(getObject(dto, "respondentSurveySettings", InvalidResponseRespondentSurveySettingsError));
      const progressPercentage = ProgressPercentage.fromDTO(getObject(dto, "progressPercentage", InvalidResponseProgressPercentageError));
      const isWelcomePageShown = getBoolean(dto, "isWelcomePageShown", InvalidResponseIsWelcomePageShownError);
      const hasRespondentConsented = getBoolean(dto, "hasRespondentConsented", InvalidResponseHasRespondentConsentedError);
      let submitConfirmationButtonText = getOptionalObject(dto, "submitConfirmationButtonText");
      if(submitConfirmationButtonText !== undefined && submitConfirmationButtonText !== null) {
        submitConfirmationButtonText = SurveyLocalizedText.fromDTO(getObject(dto, "submitConfirmationButtonText", InvalidResponseSubmitConfirmationButtonTextError));
      }
      return new Response(
        currentRespondentScreenDetails,
        respondentSurveySettings,
        progressPercentage,
        isWelcomePageShown,
        hasRespondentConsented,
        submitConfirmationButtonText
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          currentRespondentScreenDetails: this.currentRespondentScreenDetails.toDTO(),
          respondentSurveySettings: this.respondentSurveySettings.toDTO(),
          progressPercentage: this.progressPercentage.toDTO(),
          isWelcomePageShown: this.isWelcomePageShown,
          hasRespondentConsented: this.hasRespondentConsented,
      };
      if (this.submitConfirmationButtonText !== undefined && this.submitConfirmationButtonText !== null){
        dto.submitConfirmationButtonText = this.submitConfirmationButtonText.toDTO();
      }
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetCurrentQuestionAndSettings(currentRespondentScreenDetails='${this.currentRespondentScreenDetails}',respondentSurveySettings='${this.respondentSurveySettings}',progressPercentage='${this.progressPercentage}',isWelcomePageShown='${this.isWelcomePageShown}',hasRespondentConsented='${this.hasRespondentConsented}',submitConfirmationButtonText='${this.submitConfirmationButtonText}')`;
  };
  

  export enum RPCError {
    InvalidSurveyLinkId = "INVALID_SURVEY_LINK_ID",
    SurveyIsClosed = "SURVEY_IS_CLOSED"
  }

  export namespace Errors {
    
    export interface InvalidSurveyLinkIdDTO {
      code: string;
    }
    
    export class InvalidSurveyLinkId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyLinkId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyLinkId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyLinkIdDTO {
        const dto: InvalidSurveyLinkIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyLinkId.prototype.toString = function toString(): string {
      return `GetCurrentQuestionAndSettingsRPC.INVALID_SURVEY_LINK_ID()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
      surveyIsClosedTitleText: SurveyLocalizedTextDTO;
      surveyIsClosedDescriptionText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
      readonly surveyIsClosedTitleText: SurveyLocalizedText;
      readonly surveyIsClosedDescriptionText: SurveyLocalizedText;
    
      constructor(
      surveyIsClosedTitleText: SurveyLocalizedText, 
      surveyIsClosedDescriptionText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
        this.surveyIsClosedTitleText = surveyIsClosedTitleText;
        this.surveyIsClosedDescriptionText = surveyIsClosedDescriptionText;
      }
    
      static fromDTO(dto: SurveyIsClosedDTO): SurveyIsClosed {
        const surveyIsClosedTitleText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedTitleText", InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError));
        const surveyIsClosedDescriptionText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedDescriptionText", InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError));
        return new SurveyIsClosed(
          surveyIsClosedTitleText,
          surveyIsClosedDescriptionText
        );
      }
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
          surveyIsClosedTitleText: this.surveyIsClosedTitleText.toDTO(),
          surveyIsClosedDescriptionText: this.surveyIsClosedDescriptionText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `GetCurrentQuestionAndSettingsRPC.SURVEY_IS_CLOSED(surveyIsClosedTitleText='${this.surveyIsClosedTitleText}',surveyIsClosedDescriptionText='${this.surveyIsClosedDescriptionText}')`;
    };
    

    export type Errors = InvalidSurveyLinkId | SurveyIsClosed;
    export type ErrorsDTO = InvalidSurveyLinkIdDTO | SurveyIsClosedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyLinkId:
          return new InvalidSurveyLinkId();
        case RPCError.SurveyIsClosed:
          return SurveyIsClosed.fromDTO(dto as SurveyIsClosedDTO);
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyLinkIdError extends GetCurrentQuestionAndSettingsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyLinkIdError";
  }
}

export class InvalidRequestIsPreviewUrlError extends GetCurrentQuestionAndSettingsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestIsPreviewUrlError";
  }
}

export class InvalidResponseCurrentRespondentScreenDetailsError extends GetCurrentQuestionAndSettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseCurrentRespondentScreenDetailsError";
  }
}

export class InvalidResponseRespondentSurveySettingsError extends GetCurrentQuestionAndSettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentSurveySettingsError";
  }
}

export class InvalidResponseProgressPercentageError extends GetCurrentQuestionAndSettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProgressPercentageError";
  }
}

export class InvalidResponseIsWelcomePageShownError extends GetCurrentQuestionAndSettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseIsWelcomePageShownError";
  }
}

export class InvalidResponseHasRespondentConsentedError extends GetCurrentQuestionAndSettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseHasRespondentConsentedError";
  }
}

export class InvalidResponseSubmitConfirmationButtonTextError extends GetCurrentQuestionAndSettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSubmitConfirmationButtonTextError";
  }
}

export class InvalidResponseInvalidSurveyLinkIdError extends GetCurrentQuestionAndSettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyLinkIdError";
  }
}

export class InvalidResponseSurveyIsClosedError extends GetCurrentQuestionAndSettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError";
  }
}
